import React, { useState, useEffect } from 'react';
import withAuth from '../../HOC/withAuth';
import { useNavigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import dayjs from 'dayjs';
import { getTitle } from './fun';
import { isNumber } from '../../util';
import { userStore } from '../../stores/UserStore';
import http from '../../http';
import C from '../../colors';
import OrderPreviewPanel from '../../components/OrderPreviewPanel';
import { orderCreationNextStep } from '../../orderStatus';

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase',
      color: C.brand, fontWeight: 600, marginBottom: 16, marginTop: 32,
    }}>{children}</div>
  );
}

function FormRow({ label, caption, children, inline, last }) {
  return (
    <div style={{
      padding: '18px 0',
      borderBottom: last ? 'none' : `1px solid ${C.hair}`,
      display: inline ? 'grid' : 'block',
      gridTemplateColumns: inline ? '1fr auto' : undefined,
      alignItems: inline ? 'center' : undefined,
      gap: 24,
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: C.ink, marginBottom: 3 }}>{label}</div>
        {caption && <div style={{ fontSize: 12.5, color: C.ink3, lineHeight: 1.5, maxWidth: 520 }}>{caption}</div>}
      </div>
      {!inline && <div style={{ marginTop: 10 }}>{children}</div>}
      {inline && <div>{children}</div>}
    </div>
  );
}

function Toggle({ value, onChange, disabled, testId }) {
  return (
    <button data-testid={testId} type="button" onClick={() => !disabled && onChange(!value)} disabled={disabled} style={{
      width: 44, height: 24, borderRadius: 12,
      border: 'none', padding: 2,
      background: value ? C.brand : C.hair2,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      transition: 'background .2s',
      display: 'flex', alignItems: 'center',
      justifyContent: value ? 'flex-end' : 'flex-start',
    }}>
      <div style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }}/>
    </button>
  );
}

function TextInput({ name, value, onChange, placeholder, multiline, rows }) {
  const [focused, setFocused] = useState(false);
  const style = {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px',
    border: `1px solid ${focused ? C.brand : C.hair2}`,
    borderRadius: 6, outline: 'none',
    fontFamily: C.sans, fontSize: 14, color: C.ink,
    background: '#fff', transition: 'border-color .15s',
    resize: multiline ? 'vertical' : undefined,
    minHeight: multiline ? 72 : undefined,
  };
  return multiline
    ? <textarea name={name} value={value || ''} onChange={onChange} placeholder={placeholder}
        rows={rows || 2} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={style}/>
    : <input name={name} value={value || ''} onChange={onChange} placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={style}/>;
}

function NumberInput({ name, value, onChange, prefix, suffix, disabled, error, helperText }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <div style={{
        display: 'inline-flex', alignItems: 'center',
        border: `1px solid ${error ? '#e88c7c' : focused ? C.brand : C.hair2}`,
        borderRadius: 6, padding: '0 12px',
        background: disabled ? C.paper2 : '#fff',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color .15s', width: 220,
      }}>
        {prefix && <span style={{ fontSize: 14, color: C.ink3, marginRight: 4 }}>{prefix}</span>}
        <input type="number" name={name} value={value ?? ''} disabled={disabled} onChange={onChange}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 0', fontSize: 14, fontFamily: C.sans, background: 'transparent', color: C.ink, fontVariantNumeric: 'tabular-nums' }}/>
        {suffix && <span style={{ fontSize: 14, color: C.ink3, marginLeft: 4 }}>{suffix}</span>}
      </div>
      {error && <div style={{ fontSize: 12, color: '#c2412e', marginTop: 4 }}>{helperText}</div>}
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M19 12H5M11 18l-6-6 6-6"/>
    </svg>
  );
}

const CreateOrder = inject('snackbarStore', 'packageStore')(observer(({ snackbarStore, packageStore }) => {
  const navigate = useNavigate();

  const [packageId, setPackageId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shootLocation, setShootLocation] = useState('');
  const [remark, setRemark] = useState('');
  const [shootTime, setShootTime] = useState(dayjs());
  const [photoCount, setPhotoCount] = useState(0);
  const [photoCountError, setPhotoCountError] = useState('');
  const [forceLimit, setForceLimit] = useState(true);
  const [pricePerPic, setPricePerPic] = useState(0);
  const [pricePerPicError, setPricePerPicError] = useState('');
  const [isOnlinePayment, setIsOnlinePayment] = useState(false);

  useEffect(() => {
    packageStore.fetchPackages();
  }, []);

  const validatePhotoCount = (v) => {
    if (!v) return '可选照片数量不能为空';
    if (!isNumber(v)) return '必须为数字';
    if (v > 1000) return '不能超过 1000';
    return '';
  };
  const validatePricePerPic = (v) => (!isNumber(v) ? '加片价格必须为数字' : '');

  const handlePackageChange = (e) => {
    const val = e.target.value;
    setPackageId(val);
    const pkg = packageStore.packages.find(p => String(p.id) === val);
    if (pkg) setPhotoCount(pkg.photoCount);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const ce = !customerName ? '客户姓名不能为空' : '';
    const fe = validatePhotoCount(photoCount);
    const ppe = !forceLimit ? validatePricePerPic(pricePerPic) : '';
    setPhotoCountError(fe);
    setPricePerPicError(ppe);
    if (ce || fe || ppe) {
      if (ce) snackbarStore.showSnackbar(ce, 'error');
      return;
    }
    const title = getTitle('zh', customerName, shootLocation, shootTime);
    http.post('/user/order', {
      packageId: packageId || null, customerName, customerPhone, shootLocation,
      remark, shootTime, photoCount, title, pricePerPic, isOnlinePayment, forceLimit,
    }).then(res => {
      const order = res.data.order;
      const nextStep = orderCreationNextStep(order);
      navigate(`/user/orders/${order.id}/${nextStep.id}`, {
        state: nextStep.id === 'step2' ? { watermarkReview: true } : undefined,
      });
    }).catch(() => snackbarStore.showSnackbar('创建失败，请重试', 'error'));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '20px 36px 18px', background: C.paper, borderBottom: `1px solid ${C.hair}`, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `radial-gradient(700px 180px at 100% -50%, ${C.brand}24, transparent 70%)` }}/>
        <div style={{ position: 'relative' }}>
          <button onClick={() => navigate('/user/orders')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginBottom: 10, padding: '5px 10px 5px 6px',
            background: 'none', border: `1px solid ${C.hair}`,
            borderRadius: 7, cursor: 'pointer',
            fontFamily: C.sans, fontSize: 12, color: C.ink3,
          }}>
            <BackIcon/> 订单列表
          </button>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5, color: C.ink3, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
            New Order · Create
          </div>
          <h2 style={{ margin: 0, fontFamily: C.serif, fontSize: 32, lineHeight: 1.05, fontWeight: 400, color: C.ink, letterSpacing: -0.5 }}>
            创建订单
          </h2>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <form id="create-form" onSubmit={handleSubmit}>
          <div style={{ padding: '24px 44px 64px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }}>

            {/* LEFT — form */}
            <div>
              <SectionLabel>基本信息</SectionLabel>

              {packageStore.packages.length > 0 && (
                <FormRow label="套餐">
                  <select data-testid="order-package-select" name="packageId" value={packageId} onChange={handlePackageChange} style={{
                    width: '100%', padding: '9px 12px', borderRadius: 6,
                    border: `1px solid ${C.hair2}`, background: '#fff',
                    fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none',
                  }}>
                    <option value="">— 不使用套餐 —</option>
                    {packageStore.packages.map(p => (
                      <option key={p.id} value={String(p.id)}>{p.name} · {p.photoCount} 张</option>
                    ))}
                  </select>
                </FormRow>
              )}

              <FormRow label="客户姓名">
                <TextInput name="customerName" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="客户姓名"/>
              </FormRow>

              <FormRow label="客户电话">
                <TextInput name="customerPhone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="客户电话"/>
              </FormRow>

              <FormRow label="拍摄时间">
                <input type="date" value={shootTime?.format?.('YYYY-MM-DD') || shootTime || ''}
                  onChange={e => setShootTime(dayjs(e.target.value))}
                  style={{ padding: '9px 12px', borderRadius: 6, border: `1px solid ${C.hair2}`, fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none', background: '#fff' }}/>
              </FormRow>

              <FormRow label="拍摄地点">
                <TextInput name="shootLocation" value={shootLocation} onChange={e => setShootLocation(e.target.value)} placeholder="拍摄地点" multiline/>
              </FormRow>

              <FormRow label="备注">
                <TextInput name="remark" value={remark} onChange={e => setRemark(e.target.value)} placeholder="备注" multiline rows={3}/>
              </FormRow>

              <SectionLabel>选片设置</SectionLabel>

              <FormRow label="可选照片数量" caption="客户最多可以勾选的照片数量。">
                <NumberInput name="photoCount" value={photoCount} onChange={e => { setPhotoCount(e.target.value); setPhotoCountError(validatePhotoCount(e.target.value)); }}
                  suffix="张" error={!!photoCountError} helperText={photoCountError}/>
              </FormRow>

              <FormRow label="允许加片" caption="开启后客户必须选满上述数量才能提交。关闭则允许超选并按加片价格计费。" inline last={forceLimit}>
                <Toggle testId="create-allow-extra-toggle" value={!forceLimit} onChange={v => setForceLimit(!v)}/>
              </FormRow>

              {!forceLimit && (
                <>
                  <FormRow label="加片价格" caption="客户超出上限的每张照片将按此价格计费。">
                    <NumberInput name="pricePerPic" value={pricePerPic} onChange={e => { setPricePerPic(e.target.value); setPricePerPicError(validatePricePerPic(e.target.value)); }}
                      prefix="¥" suffix="/张" error={!!pricePerPicError} helperText={pricePerPicError}/>
                  </FormRow>

                  <FormRow label="平台代收款" caption="如需平台代收款，请先绑定微信账号。" inline last>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <Toggle testId="create-online-payment-toggle" value={isOnlinePayment} disabled={!userStore.wxOpenId} onChange={v => setIsOnlinePayment(v)}/>
                      {!userStore.wxOpenId && (
                        <button data-testid="create-wechat-bind" type="button" onClick={() => userStore.setWechatDialogOpen(true)} style={{
                          padding: '8px 14px', borderRadius: 6, background: C.ink, color: '#fff',
                          border: 'none', fontFamily: C.sans, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        }}>绑定微信</button>
                      )}
                    </div>
                  </FormRow>
                </>
              )}
            </div>

            {/* RIGHT — preview */}
            <OrderPreviewPanel showImageName={false} forceLimit={forceLimit} isOnlinePayment={isOnlinePayment} price={pricePerPic} photoCount={photoCount} hasPackage={Boolean(packageId)}/>
          </div>
        </form>
      </div>

      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${C.hair}`,
        padding: '14px 36px',
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
        background: C.paper, flexShrink: 0,
      }}>
        <button form="create-form" type="submit" style={{
          fontFamily: C.sans, fontSize: 14, fontWeight: 500,
          padding: '10px 24px', borderRadius: 7,
          background: C.brand, color: '#fff', border: 'none',
          cursor: 'pointer',
          boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
        }}>
          创建订单
        </button>
      </footer>
    </div>
  );
}));

export default withAuth(CreateOrder);
